import React, { useState, useRef, useEffect } from "react";
import { TextField, InputAdornment, Paper, List, ListItem, ListItemText, ClickAwayListener } from "@material-ui/core";
import { Search } from "@material-ui/icons";
import { useTheme } from "@material-ui/core/styles";

/**
 * Reusable Custom Dropdown Component with Search
 * 
 * @param {Object} props
 * @param {string} props.value - Selected value
 * @param {function} props.onChange - Callback function when selection changes: (event, selectedItem) => void
 * @param {Array} props.items - Array of items to display in dropdown
 * @param {function} props.getItemLabel - Function to get display label from item: (item) => string
 * @param {function} props.getItemValue - Function to get value from item: (item) => any
 * @param {function} props.filterItems - Function to filter items based on search: (items, searchTerm) => filteredItems
 * @param {string} props.label - Label for the dropdown
 * @param {string} props.id - HTML id attribute
 * @param {boolean} props.required - Whether the field is required
 * @param {boolean} props.disabled - Whether the field is disabled
 * @param {boolean} props.readOnly - Whether the field is read-only
 * @param {string} props.size - Size of the input ("small", "medium")
 * @param {string} props.variant - Variant of the input ("outlined", "filled", "standard")
 * @param {Object} props.sx - Additional styling
 * @param {string} props.helperText - Helper text below the field
 * @param {boolean} props.error - Whether to show error state
 * @param {string} props.placeholder - Placeholder text when dropdown is open
 * @param {boolean} props.showClearOption - Whether to show "Clear selection" option
 * @param {string} props.clearOptionLabel - Label for clear option (default: "Clear selection")
 * @param {string} props.noResultsLabel - Label when no results found (default: "No results found")
 * @param {function} props.renderItem - Custom render function for list items: (item, isSelected) => ReactNode
 * @param {boolean} props.autoWidth - Whether to auto-adjust width based on selected value (default: false)
 * @returns {JSX.Element}
 */
const CustomDropdown = ({
  value = "",
  onChange,
  items = [],
  getItemLabel,
  getItemValue,
  filterItems,
  label = "Select",
  id = "custom-dropdown",
  required = false,
  disabled = false,
  readOnly = false,
  size = "small",
  variant = "outlined",
  sx = {},
  helperText = "",
  error = false,
  placeholder = "Search...",
  showClearOption = true,
  clearOptionLabel = "Clear selection",
  noResultsLabel = "No results found",
  renderItem = null,
  autoWidth = false,
  ...otherProps
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [displayValue, setDisplayValue] = useState("");
  const [tempSearchValue, setTempSearchValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [inputWidth, setInputWidth] = useState(null);
  const [initialWidth, setInitialWidth] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const measureRef = useRef(null);
  const theme = useTheme();

  // Capture initial width of the input field
  useEffect(() => {
    if (autoWidth && inputRef.current && initialWidth === null) {
      const currentWidth = inputRef.current.offsetWidth;
      setInitialWidth(currentWidth);
    }
  }, [autoWidth, initialWidth]);

  // Initialize display value based on selected value
  useEffect(() => {
    if (!isSearching) {
      if (value) {
        const selectedItem = items?.find(item => getItemValue(item) === value);
        setDisplayValue(selectedItem ? getItemLabel(selectedItem) : "");
      } else {
        setDisplayValue("");
      }
    }
  }, [value, items, getItemValue, getItemLabel, isSearching]);

  // Measure width of display value when autoWidth is enabled
  useEffect(() => {
    if (autoWidth && measureRef.current && displayValue) {
      const width = measureRef.current.offsetWidth;
      // Add padding for icon, adornments, and some breathing room
      // Increased from 80 to 110 to prevent text cutoff
      const adjustedWidth = width + 110;
      // Never go smaller than initial width or minimum 150px
      const finalWidth = Math.max(adjustedWidth, initialWidth || 150, 150);
      setInputWidth(finalWidth);
    } else if (autoWidth && !displayValue) {
      // Set minimum width when empty, respecting initial width
      setInputWidth(Math.max(initialWidth || 150, 150));
    }
  }, [displayValue, autoWidth, initialWidth]);
  
  // Filter items based on search term
  const filteredItems = filterItems ? filterItems(items, tempSearchValue) : items;

  const handleInputChange = (event) => {
    if (readOnly) return; // Don't allow changes in read-only mode
    
    const inputValue = event.target.value;
    setTempSearchValue(inputValue);
    setSearchTerm(inputValue);
    setDisplayValue(inputValue);
    setIsSearching(true);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (readOnly) return; // Don't open dropdown in read-only mode
    
    setIsOpen(true);
    setIsSearching(true);
    setDisplayValue("");
    setTempSearchValue("");
    setSearchTerm("");
  };

  const handleItemSelect = (item) => {
    if (readOnly) return; // Don't allow selection in read-only mode
    
    setSearchTerm("");
    setTempSearchValue("");
    setDisplayValue(getItemLabel(item));
    setIsOpen(false);
    setIsSearching(false);
    
    // Call the onChange callback
    if (onChange) {
      const event = { target: { value: getItemValue(item) } };
      onChange(event, item);
    }
  };

  const handleClickAway = () => {
    setIsOpen(false);
    setSearchTerm("");
    setTempSearchValue("");
    setIsSearching(false);
    if (value) {
      const selectedItem = items?.find(item => getItemValue(item) === value);
      setDisplayValue(selectedItem ? getItemLabel(selectedItem) : "");
    } else {
      setDisplayValue("");
    }
  };

  const handleClearSelection = () => {
    setSearchTerm("");
    setTempSearchValue("");
    setDisplayValue("");
    setIsOpen(false);
    setIsSearching(false);
    if (onChange) {
      const clearEvent = { target: { value: "" } };
      onChange(clearEvent, null);
    }
  };

  const handleKeyDown = (event) => {
    if (readOnly) return; // Don't handle keyboard events in read-only mode
    
    if (event.key === 'Escape') {
      handleClickAway();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (displayValue === "" || searchTerm === "") {
        // If input is empty, clear the selection
        handleClearSelection();
      } else if (filteredItems.length > 0) {
        // If there's text and matches, select the first one
        handleItemSelect(filteredItems[0]);
      }
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div style={{ position: 'relative', height: '40px', width: autoWidth && inputWidth ? `${inputWidth}px` : undefined, ...sx }}>
        {/* Hidden element for measuring text width */}
        {autoWidth && (
          <span
            ref={measureRef}
            style={{
              position: 'absolute',
              visibility: 'hidden',
              whiteSpace: 'nowrap',
              fontSize: '1rem',
              fontFamily: 'inherit',
              padding: '0',
            }}
          >
            {displayValue || placeholder}
          </span>
        )}
        <TextField
          ref={inputRef}
          label={label}
          id={id}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          size={size}
          variant={variant}
          required={required}
          disabled={disabled}
          error={error}
          helperText={helperText}
          placeholder={isOpen && !readOnly ? placeholder : ""}
          fullWidth={!autoWidth}
          style={autoWidth && inputWidth ? { width: `${inputWidth}px` } : undefined}
          InputProps={{
            readOnly: readOnly,
            style: {
              fontSize: "1rem",
              height: "40px",
              paddingRight: "32px",
              width: autoWidth && inputWidth ? `${inputWidth}px` : undefined,
            },
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          InputLabelProps={{
            style: {
              fontSize: "1rem",
            },
          }}
          {...otherProps}
        />
        
        {/* Custom dropdown arrow */}
        <div
          style={{
            position: 'absolute',
            right: '8px',
            top: '50%',
            transform: `translateY(-50%) ${isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}`,
            transition: 'transform 0.2s',
            cursor: readOnly || disabled ? 'default' : 'pointer',
            zIndex: 1001,
            pointerEvents: readOnly || disabled ? 'none' : 'auto',
            backgroundColor: 'white',
            padding: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={() => !readOnly && !disabled && setIsOpen(!isOpen)}
        >
          <span
            style={{
              fontSize: '16px',
              color: readOnly ? theme.palette.dropdownTriangle[300] : theme.palette.dropdownTriangle[400],
              userSelect: 'none',
              lineHeight: 1
            }}
          >
            ⏷
          </span>
        </div>
        
        {isOpen && !readOnly && !disabled && (
          <Paper 
            ref={dropdownRef}
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: 300,
              overflow: 'auto',
              marginTop: 4,
              border: '1px solid #ccc',
            }}
          >
            <List dense>
              {showClearOption && value && (
                <ListItem 
                  button 
                  onClick={handleClearSelection}
                  style={{
                    backgroundColor: 'transparent',
                    borderBottom: '1px solid #eee',
                    fontStyle: 'italic'
                  }}
                >
                  <ListItemText 
                    primary={clearOptionLabel}
                    style={{ fontSize: '0.875rem', color: '#666' }}
                  />
                </ListItem>
              )}
              {filteredItems.length > 0 ? (
                filteredItems.map((item) => {
                  const itemValue = getItemValue(item);
                  const isSelected = itemValue === value;
                  
                  if (renderItem) {
                    return (
                      <div key={itemValue} onClick={() => handleItemSelect(item)}>
                        {renderItem(item, isSelected)}
                      </div>
                    );
                  }
                  
                  return (
                    <ListItem 
                      key={itemValue} 
                      button 
                      onClick={() => handleItemSelect(item)}
                      style={{
                        backgroundColor: isSelected ? '#f0f0f0' : 'transparent',
                      }}
                    >
                      <ListItemText 
                        primary={getItemLabel(item)}
                        style={{ fontSize: '0.875rem' }}
                      />
                    </ListItem>
                  );
                })
              ) : (
                <ListItem disabled>
                  <ListItemText 
                    primary={`${noResultsLabel}${searchTerm ? ` matching "${searchTerm}"` : ''}`}
                    style={{ fontStyle: 'italic', color: '#999' }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default CustomDropdown;
